import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import TreeLogo from '@/components/TreeLogo';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(45 25% 97%)' }}>
      {/* Header */}
      <nav className="sticky top-0 z-50" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(15 35% 88%)' }}>
              <TreeLogo size={24} className="text-slate-600" />
            </div>
            <h1 className="text-lg text-white font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
              Rooted
            </h1>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            className="rounded-full text-white/80 hover:text-white hover:bg-white/10"
            onClick={() => navigate(-1)}
          >
            ← Back
          </Button>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-4xl mb-8" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
          Privacy Policy
        </h1>
        
        <div className="prose prose-slate max-w-none" style={{ color: 'hsl(220 12% 40%)' }}>
          <p className="text-sm mb-8" style={{ color: 'hsl(220 12% 55%)' }}>
            Last updated: January 2025
          </p>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              Introduction
            </h2>
            <p className="mb-4 leading-relaxed">
              Welcome to Rooted ("we," "our," or "us"). We are committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our devotional application and website.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              Information We Collect
            </h2>
            <h3 className="text-lg font-medium mb-2" style={{ color: 'hsl(250 15% 40%)' }}>Personal Information</h3>
            <p className="mb-4 leading-relaxed">
              When you create an account, we may collect:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Name and email address</li>
              <li>Password (stored securely using encryption)</li>
              <li>Optional demographic information (age range, gender, faith background)</li>
              <li>Payment information (processed securely through Stripe)</li>
            </ul>

            <h3 className="text-lg font-medium mb-2" style={{ color: 'hsl(250 15% 40%)' }}>Usage Information</h3>
            <p className="mb-4 leading-relaxed">
              We automatically collect certain information when you use our service:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Device and browser information</li>
              <li>IP address and approximate location</li>
              <li>Pages visited and features used</li>
              <li>Reading streaks and engagement patterns</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              How We Use Your Information
            </h2>
            <p className="mb-4 leading-relaxed">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Provide and maintain our devotional service</li>
              <li>Process your subscription and payments</li>
              <li>Send you daily devotional reminders (if opted in)</li>
              <li>Personalize your experience and track your spiritual journey</li>
              <li>Improve our content and features</li>
              <li>Communicate with you about your account or service updates</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              Data Security
            </h2>
            <p className="mb-4 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal information, including:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Encryption of sensitive data in transit and at rest</li>
              <li>Secure password hashing</li>
              <li>Regular security assessments</li>
              <li>Limited access to personal information by authorized personnel only</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              Your Rights
            </h2>
            <p className="mb-4 leading-relaxed">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Access and receive a copy of your personal data</li>
              <li>Correct inaccurate personal information</li>
              <li>Request deletion of your account and data</li>
              <li>Opt out of marketing communications</li>
              <li>Disable email reminders at any time</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              Third-Party Services
            </h2>
            <p className="mb-4 leading-relaxed">
              We use trusted third-party services to operate our platform:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Stripe</strong> - For secure payment processing</li>
              <li><strong>Google</strong> - For optional social login authentication</li>
              <li><strong>OpenAI</strong> - For audio text-to-speech features</li>
            </ul>
            <p className="mb-4 leading-relaxed">
              These services have their own privacy policies governing their use of your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              Cookies
            </h2>
            <p className="mb-4 leading-relaxed">
              We use essential cookies to maintain your session and remember your preferences. These cookies are necessary for the service to function properly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              Children's Privacy
            </h2>
            <p className="mb-4 leading-relaxed">
              Our service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              Changes to This Policy
            </h2>
            <p className="mb-4 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              Contact Us
            </h2>
            <p className="mb-4 leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="mb-4 leading-relaxed">
              <strong>Email:</strong> privacy@rootedfaithjournal.com
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-8" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm text-white/70">&copy; 2025 Rooted. A space for contemplation and spiritual growth.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
